import { createContext, useContext } from 'react';

import type { TaskVariable } from '@/types/experience';

export const TaskVariablesContext = createContext<TaskVariable[]>([]);
export const useTaskVariables = () => useContext(TaskVariablesContext);

/**
 * The one set of rolled variable values shared by every block in Preview.
 *
 * Variables are task-scoped, so a student gets ONE value per variable per attempt — a task whose
 * first block reads `{n}` and whose second reads `{n}+10` has to agree about `n`. Blocks used to seed
 * Preview from their own private roll (`if (isSelected || isPreview) setRolledValues(...)`), which made
 * them disagree: measured live, one block rendered n=7 while another rendered n=9 in the same Preview.
 *
 * This is deliberately Preview-only. The three display modes each resolve differently and that is by
 * design, not an inconsistency to iron out:
 * - display (unselected canvas card) — chips are NOT resolved, they stay chips
 * - block test (selected card / phone Test tab) — each block keeps its OWN roll plus a reroll, so a
 *   teacher can step through many combinations for the one block they're testing
 * - Preview — this context, one coherent roll for the whole task
 *
 * `null` means "not in Preview", which is what lets a block tell the difference between "Preview with
 * no variables defined" (an empty map) and "not previewing at all".
 */
export const TaskPreviewRollContext = createContext<Record<string, string> | null>(null);
export const useTaskPreviewRoll = () => useContext(TaskPreviewRollContext);
